import React from 'react';
import { Space, Button, GlobalToken } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { EPSelect, EPSelectOption } from '@/components';
import PlusIcon from '@/comp/common/icons/PlusIcon';
import { t } from 'i18next';
import { getSensorName } from './SensorsKPITab.utils';
import { SensorsAvailabilityEnum } from '@/store/Engine/constants';
import DevicesStore from '@/store/Devices/DevicesStore';
import { EngineStore } from '@/store/Engine/EngineStore';
import { KPISensorRowContainer } from './SensorsKPITab.styles';

interface KPISensorRowProps {
  priority;
  showDeleteButton?: boolean;
  logicOptions: React.ReactNode[];
  sensorsOptions: Array<{ label: string; value: string }>;
  handleStatus: (key: string) => 'error' | undefined;
  onSensorPriorityFieldChange: (key: string, field: string) => void;
  onDeleteIconClick: (key: string) => void;
  devicesStore: DevicesStore;
  engineStore: EngineStore;
  type: string;
  token: GlobalToken;
  isAlwaysReadOnly?: boolean;
}

const KPISensorRow: React.FC<KPISensorRowProps> = ({
  priority,
  showDeleteButton = false,
  logicOptions,
  sensorsOptions,
  handleStatus,
  onSensorPriorityFieldChange,
  onDeleteIconClick,
  devicesStore,
  engineStore,
  type,
  token,
  isAlwaysReadOnly = false,
}) => {
  if (!priority) return null;

  return (
    <KPISensorRowContainer $isReadOnly={isAlwaysReadOnly}>
      <Space direction="vertical" className="space-vertical">
        <div className="flex-wrap">
          <div className="flex-1">
            <span className="section-row-title">{t('logic')}</span>
            <EPSelect
              value={priority?.aggregation}
              onChange={onSensorPriorityFieldChange(priority?.key, 'aggregation')}
              className="styled-select"
            >
              {logicOptions}
            </EPSelect>
          </div>
          <div className="flex-1">
            <span className="section-row-title">{t('online')}</span>
            <EPSelect
              value={priority?.availability}
              onChange={onSensorPriorityFieldChange(priority?.key, 'availability')}
              className="styled-select"
            >
              <EPSelectOption value={SensorsAvailabilityEnum.Any} key={SensorsAvailabilityEnum.Any}>
                {t('any')}
              </EPSelectOption>
              <EPSelectOption value={SensorsAvailabilityEnum.All} key={SensorsAvailabilityEnum.All}>
                {t('all')}
              </EPSelectOption>
            </EPSelect>
          </div>
          <div className="flex-2">
            <span className="section-row-title">{t('sensors')}</span>
            <div className="sensor-container">
              <EPSelect
                mode="multiple"
                className="styled-select"
                value={priority?.sensors?.map(sensor => ({
                  value: sensor?.id,
                  label: getSensorName(devicesStore, engineStore, sensor?.id, type),
                }))}
                status={handleStatus(priority?.key)}
                options={sensorsOptions}
                onChange={onSensorPriorityFieldChange(priority?.key, 'sensors')}
                suffixIcon={<PlusIcon color={token.colorBorder} />}
                filterOption={(input, option) =>
                  option?.label && option.label?.toLowerCase()?.includes(input?.toLocaleLowerCase())
                }
              />
              {showDeleteButton && (
                <Button
                  type="text"
                  className="delete-button"
                  icon={<DeleteOutlined />}
                  onClick={() => onDeleteIconClick(priority?.key)}
                />
              )}
            </div>
          </div>
        </div>
      </Space>
    </KPISensorRowContainer>
  );
};

export default React.memo(KPISensorRow);
